export function slugify(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function stripHtml(value = "") {
  return String(value)
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function truncateText(value = "", max = 180) {
  const clean = stripHtml(value);

  if (clean.length <= max) return clean;

  return `${clean.slice(0, max - 1)}…`;
}

export function formatDateTime(value) {
  if (!value) return "Date inconnue";

  const raw = String(value).trim();

  const match = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/,
  );

  if (!match) return "Date inconnue";

  const [, yyyy, mm, dd, hh, mi] = match;

  if (!hh || !mi) {
    return `${dd}/${mm}/${yyyy}`;
  }

  return `${dd}/${mm}/${yyyy} à ${hh}:${mi}`;
}

export function normalizeExternalUrl(url = "") {
  const clean = String(url || "").trim();

  if (!clean) return "";

  if (
    clean.startsWith("http://") ||
    clean.startsWith("https://") ||
    clean.startsWith("mailto:") ||
    clean.startsWith("tel:")
  ) {
    return clean;
  }

  if (clean.startsWith("/")) return clean;

  return `https://${clean}`;
}

export function getServiceAddress(item) {
  const parts = [
    item?.adresse_service,
    [item?.cp_service, item?.ville_service].filter(Boolean).join(" "),
  ].filter(Boolean);

  return parts.join("\n");
}

export function getGoogleMapsUrl(gps = "") {
  const clean = String(gps || "").trim();

  if (!clean) return "";

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    clean,
  )}`;
}

export function parseEmails(value = "") {
  return String(value || "")
    .split(/[;,]/)
    .map((email) => email.trim())
    .filter(Boolean);
}

export function isValidEmail(email = "") {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());
}

export function isValidGps(value = "") {
  const clean = String(value || "").trim();

  if (!clean) return true;

  const parts = clean
    .split(/[;,]/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length !== 2) return false;

  const lat = Number(parts[0]);
  const lng = Number(parts[1]);

  if (Number.isNaN(lat) || Number.isNaN(lng)) return false;
  if (lat < -90 || lat > 90) return false;
  if (lng < -180 || lng > 180) return false;

  return true;
}

export function getServiceTypeLabel(type = "") {
  const map = {
    service: "Service",
    equipement: "Équipement",
    partenaire: "Partenaire",
    archives: "Archives",
  };

  return map[type] || "Non défini";
}

export function buildServicesMunicipauxSearchParams({
  q,
  categorie,
  typeFilter,
  stateFilter,
  publicFilter,
  withGps,
  page,
  limit,
}) {
  return {
    q: q?.trim() || undefined,
    categorie: categorie?.trim() || undefined,
    type_service: typeFilter === "all" ? undefined : String(typeFilter).trim(),
    is_active:
      stateFilter === "all"
        ? undefined
        : stateFilter === "active"
          ? true
          : false,
    is_public:
      publicFilter === "all"
        ? undefined
        : publicFilter === "public"
          ? true
          : false,
    with_gps: withGps === "yes" ? true : undefined,
    page,
    limit,
    sort: "ordre",
    dir: "ASC",
  };
}

export function normalizeServiceMunicipalForm(form) {
  const name = String(form.nom_service || "").trim();
  const slug = String(form.slug_service || "").trim() || slugify(name);

  const parentIdNum = parseInt(form.parent_id, 10);

  return {
    nom_service: name,
    slug_service: slug,
    categorie_service: form.categorie_service?.trim() || "",
    type_service: form.type_service?.trim() || "",
    parent_id: !Number.isNaN(parentIdNum) ? parentIdNum : null,
    accroche_service: form.accroche_service?.trim() || "",
    description_service: form.description_service || "",
    adresse_service: form.adresse_service?.trim() || "",
    cp_service: form.cp_service?.trim() || "",
    ville_service: form.ville_service?.trim() || "",
    gps_service: form.gps_service?.trim() || "",
    tel_service: form.tel_service?.trim() || "",
    tel_secondaire: form.tel_secondaire?.trim() || "",
    mail_service: form.mail_service?.trim() || "",
    website_service: form.website_service?.trim() || "",
    autre_url_service: form.autre_url_service?.trim() || "",
    horaires_service: form.horaires_service || "",
    infos_complementaires: form.infos_complementaires || "",
    icone_service: form.icone_service?.trim() || "",
    ordre_affichage: Number(form.ordre_affichage) || 0,
    is_public: !!form.is_public,
    is_active: !!form.is_active,
  };
}

export function validateServiceMunicipalForm(form) {
  const errors = {};

  const name = String(form.nom_service || "").trim();
  const slug = String(form.slug_service || "").trim() || slugify(name);

  if (!name) {
    errors.nom_service = "Le nom du service est obligatoire.";
  }

  if (!slug) {
    errors.slug_service = "Le slug est obligatoire.";
  }

  if (form.mail_service?.trim()) {
    const emails = parseEmails(form.mail_service);
    const invalidEmail = emails.find((email) => !isValidEmail(email));

    if (invalidEmail) {
      errors.mail_service = `Email invalide : ${invalidEmail}`;
    }
  }

  if (form.gps_service?.trim() && !isValidGps(form.gps_service)) {
    errors.gps_service =
      "Format attendu : latitude, longitude. Exemple : 44.8742, -0.6413";
  }

  return errors;
}

export function getServicesMunicipauxStats(items = [], total = 0) {
  const activeOnPage = items.filter((item) => !!item.is_active).length;
  const inactiveOnPage = items.filter((item) => !item.is_active).length;
  const publicOnPage = items.filter((item) => !!item.is_public).length;
  const hiddenOnPage = items.filter((item) => !item.is_public).length;
  const withGpsOnPage = items.filter((item) => !!item.gps_service).length;
  const withContactOnPage = items.filter(
    (item) => !!item.mail_service || !!item.tel_service,
  ).length;
  const equipmentsOnPage = items.filter(
    (item) => item.type_service === "equipement",
  ).length;

  return {
    total,
    activeOnPage,
    inactiveOnPage,
    publicOnPage,
    hiddenOnPage,
    withGpsOnPage,
    withContactOnPage,
    equipmentsOnPage,
  };
}